<script lang="ts">
  import { onMount } from "svelte";

  import type { GetExternalContactsResponse, IntegrationDetails } from "../../helpers/types";

  import {
    externalContactsStore,
    setSetupStep,
    setupStepStore,
    integrationHasSites,
  } from "../../stores/integrations-store";
  import { getExternalContacts, getIntegrations } from "../../helpers/integrations-api";
  import SupplyLines from "./SupplyLines.svelte";
  import Notifications from "./Notifications.svelte";
  import ConnectAccount from "./ConnectAccount.svelte";
  import Site from "./Site.svelte";
  import { track } from "../../helpers/mixpanel";
  import LoadingSpinner from "../../components/LoadingSpinner.svelte";
  import CloseIcon from "../icons/CloseIcon.svelte";
  import { selectedBuyer } from "../../stores/buyer";

  export let integration: IntegrationDetails;
  export let onClose: () => void;
  let externalContacts: GetExternalContactsResponse | null;

  externalContactsStore.subscribe((contacts) => {
    externalContacts = contacts;
  });
  let currentStep = 0;

  setupStepStore.subscribe((value) => {
    currentStep = value;
  });

  onMount(() => {
    setSetupStep(integration);
  });

  const handleAuthComplete = (isOauth: boolean) => {
    if (isOauth) {
      track("integration_oauth_closed", {
        buyer_id: $selectedBuyer?.id,
        buyer_name: $selectedBuyer?.name,
        integration_id: integration.id,
        platform: integration.platform,
        type: "setup",
      });
    }
    getExternalContacts(integration.id);
    getIntegrations(integration.buyer_id);
    setSetupStep(integration);
  };

  const handleSiteSelected = (siteId: string) => {
    integration.site_id = siteId;
    getExternalContacts(integration.id);
    getIntegrations(integration.buyer_id);
    setSetupStep(integration);
  };
</script>

<div class="container">
  <div class="header">
    <button on:click={onClose}><CloseIcon /></button>
  </div>
  {#if !externalContacts}
    <div class="loading-container">
      <div class="loading-spinner">
        <LoadingSpinner size={50} />
      </div>
    </div>
  {:else}
    <ConnectAccount
      {integration}
      isConnected={!!externalContacts?.authenticated}
      onPopupClosed={(isOauth) => handleAuthComplete(isOauth)}
      stepNumber={1}
      isActive={currentStep === 1}
    />
    {#if integrationHasSites(integration?.platform)}
      <Site
        {integration}
        contacts={externalContacts?.result || null}
        onSiteSelected={(siteId) => handleSiteSelected(siteId)}
        stepNumber={2}
        {currentStep}
        authenticated={externalContacts?.authenticated}
        isActive={currentStep === 2}
      />
    {/if}
    <SupplyLines
      {integration}
      contacts={externalContacts?.result || null}
      stepNumber={!integrationHasSites(integration?.platform) ? 2 : 3}
      {currentStep}
      isActive={currentStep === (!integrationHasSites(integration?.platform) ? 2 : 3)}
      authenticated={externalContacts?.authenticated}
    />
    <Notifications
      authenticated={externalContacts?.authenticated}
      {integration}
      {currentStep}
      stepNumber={!integrationHasSites(integration?.platform) ? 3 : 4}
      isActive={currentStep === (!integrationHasSites(integration?.platform) ? 3 : 4)}
    />
    {#if currentStep === (!integrationHasSites(integration?.platform) ? 4 : 5)}
      <div class="setup-complete">
        <img class="issue_icon" alt={`logo`} src="static/assets/icons/tick_success_icon.svg" />
        <h4 class="setup-complete-text">
          setup all done, your invoices will integrate with {integration.platform} now.
        </h4>
        {#if integration.platform === "Xero"}
          <h5 class="setup-complete-text">
            use tracking categories? you can map them by clicking 'manage'
          </h5>
        {/if}
      </div>
    {/if}
  {/if}
</div>

<style>
  .container {
    border-top: 1px solid;
    padding: 18px;
    border-color: var(--rekki-neutral-01);
  }

  .header {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
  }

  .header button {
    border: 0 none;
    background-color: transparent;
    padding-bottom: 24px;
    margin: 0;
  }

  .setup-complete {
    display: flex;
    flex-direction: column;
    background: white;
    padding: 40px;
    align-items: center;
    border-radius: 8px;
  }

  .setup-complete-text {
    font-size: 18px;
    font-weight: normal;
  }

  .issue_icon {
    height: 60px;
    margin-bottom: 20px;
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 75vh;
    width: 100%;
  }
</style>
